import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@pooflabs/web';
import { PageLayout } from '@/components/poof-ui';
import { Button } from '@/components/ui/button';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeAllLocks } from '@/lib/collections/locks';
import type { LocksResponse } from '@/lib/collections/locks';
import { subscribeAllDonations } from '@/lib/collections/donations';
import type { DonationsResponse } from '@/lib/collections/donations';
import { AuroraBackground } from '@/components/effects/AuroraBackground';
import { Particles } from '@/components/effects/Particles';
import { BlurFade } from '@/components/effects/BlurFade';
import NavHeader from '@/components/NavHeader';
import { buildLeaderboard, formatPoints, truncateAddress } from '@/utils/pointsCalculator';
import { warmUsdPrices } from '@/utils/usdPricing';
import { SOL, BONK, SKR, MEW, ANGELIC_SOL, GMT, DRAGON } from '@/utils/tokenConfig';
import { Lock, Clock, Shield, TrendingUp, Skull, Trophy, Heart, Coins } from 'lucide-react';
import { PriceTicker } from '@/components/PriceTicker';

const HowItWorksStep = ({
  number,
  title,
  description,
  icon,
  delay,
}: {
  number: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  delay: number;
}) => (
  <BlurFade delay={delay}>
    <div className="relative group">
      <div
        className="relative overflow-hidden rounded-lg border p-8 h-full"
        style={{
          background: 'linear-gradient(135deg, hsl(30 6% 7%) 0%, hsl(30 6% 9%) 100%)',
          borderColor: 'hsl(35 12% 14%)',
        }}
      >
        <div
          className="absolute top-0 right-0 w-24 h-24 opacity-10 group-hover:opacity-20 transition-opacity duration-500"
          style={{
            background: 'radial-gradient(circle at top right, hsl(38 65% 57%), transparent)',
          }}
        />
        <div className="flex items-start gap-5">
          <div
            className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold"
            style={{
              background: 'hsl(38 65% 57% / 0.12)',
              border: '1px solid hsl(38 65% 57% / 0.3)',
              color: 'hsl(38 65% 57%)',
              fontFamily: "'Cormorant Garamond', serif",
            }}
          >
            {number}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span style={{ color: 'hsl(38 65% 57%)' }}>{icon}</span>
              <h3
                className="text-lg font-semibold"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {title}
              </h3>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: 'hsl(35 8% 52%)' }}>
              {description}
            </p>
          </div>
        </div>
      </div>
    </div>
  </BlurFade>
);

// Nav handled by shared NavHeader component

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();

  // Leaderboard points below read from usdPricing's module-level cache synchronously,
  // which isn't itself reactive — warm it (staggered, so we don't burst Jupiter) and
  // force a re-render as each price resolves so points update out of "pending".
  const [, bumpPriceTick] = useState(0);
  useEffect(() => {
    const cancel = warmUsdPrices([SOL, BONK, SKR, MEW, ANGELIC_SOL, GMT, DRAGON], () => {
      bumpPriceTick((n) => n + 1);
    });
    return cancel;
  }, []);

  const { data: allLocks } = useRealtimeData<LocksResponse[]>(
    subscribeAllLocks,
    true
  );

  const { data: allDonations } = useRealtimeData<DonationsResponse[]>(
    subscribeAllDonations,
    true
  );

  // Leaderboard preview (includes donation points for accurate ranking)
  const leaderboard = buildLeaderboard(allLocks ?? [], allDonations ?? []);
  const top5 = leaderboard.slice(0, 5);

  const handleStart = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      login();
    }
  };

  return (
    <PageLayout fullBleed footer={false}>
      {/* Nav */}
      <NavHeader />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <AuroraBackground color="hsl(38 65% 57%)" />
        <Particles quantity={40} color="hsl(38 65% 57%)" />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto pt-20">
          <BlurFade delay={0.1}>
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium mb-8 tracking-widest uppercase"
              style={{
                background: 'hsl(38 65% 57% / 0.1)',
                border: '1px solid hsl(38 65% 57% / 0.25)',
                color: 'hsl(43 75% 68%)',
              }}
            >
              <Lock size={10} />
              Solana Escrow Protocol
            </div>
          </BlurFade>

          <BlurFade delay={0.2}>
            <h1
              className="mb-6 leading-none"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 'clamp(3.5rem, 8vw, 7rem)',
                fontWeight: 600,
                letterSpacing: '-0.02em',
                background:
                  'linear-gradient(135deg, hsl(40 15% 92%) 0%, hsl(43 75% 68%) 50%, hsl(38 65% 57%) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              SelfWorth
            </h1>
          </BlurFade>

          <BlurFade delay={0.3}>
            <p
              className="text-xl md:text-2xl mb-4 font-light italic"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                color: 'hsl(40 15% 72%)',
                letterSpacing: '0.01em',
              }}
            >
              Value what you have. Hold onto it.
            </p>
          </BlurFade>

          <BlurFade delay={0.4}>
            <p
              className="text-base mb-8 max-w-xl mx-auto leading-relaxed"
              style={{ color: 'hsl(35 8% 52%)' }}
            >
              Lock SOL, BONK, SKR, MEW, USDC, ANGEL, GMT, or DRAGON in a time-sealed vault and prove to yourself — and the world — that you have
              the discipline to hold. Every lock is a commitment on-chain.
            </p>
          </BlurFade>

          <BlurFade delay={0.45}>
            <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
              {[
                { symbol: 'SOL',   color: 'hsl(43 75% 68%)',  bg: 'hsl(38 65% 57% / 0.12)',  border: 'hsl(38 65% 57% / 0.35)' },
                { symbol: 'BONK',  color: 'hsl(36 92% 55%)',  bg: 'hsl(36 85% 50% / 0.12)',  border: 'hsl(36 85% 50% / 0.35)' },
                { symbol: 'SKR',   color: 'hsl(168 80% 48%)', bg: 'hsl(168 70% 42% / 0.12)', border: 'hsl(168 70% 42% / 0.35)' },
                { symbol: 'MEW',   color: 'hsl(280 75% 65%)', bg: 'hsl(280 65% 55% / 0.12)', border: 'hsl(280 65% 55% / 0.35)' },
                { symbol: 'USDC',  color: 'hsl(214 72% 60%)', bg: 'hsl(214 65% 52% / 0.12)', border: 'hsl(214 65% 52% / 0.35)' },
                { symbol: 'ANGEL', color: 'hsl(280 80% 72%)', bg: 'hsl(280 70% 60% / 0.12)', border: 'hsl(280 70% 60% / 0.35)' },
                { symbol: 'GMT',   color: 'hsl(142 65% 55%)', bg: 'hsl(142 55% 45% / 0.12)', border: 'hsl(142 55% 45% / 0.35)' },
                { symbol: 'DRAGON', color: 'hsl(8 85% 58%)',  bg: 'hsl(8 75% 48% / 0.12)',    border: 'hsl(8 75% 48% / 0.35)' },
              ].map((t) => (
                <span
                  key={t.symbol}
                  className="px-3 py-1 rounded-full text-xs font-bold tracking-wide"
                  style={{ background: t.bg, color: t.color, border: `1px solid ${t.border}` }}
                >
                  {t.symbol}
                </span>
              ))}
            </div>
          </BlurFade>

          <BlurFade delay={0.5}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                onClick={handleStart}
                className="px-10 py-6 text-base font-medium tracking-wide"
                style={{
                  background: 'linear-gradient(135deg, hsl(38 65% 52%), hsl(43 75% 60%))',
                  color: 'hsl(30 8% 4%)',
                  border: 'none',
                  boxShadow: '0 0 40px hsl(38 65% 57% / 0.3)',
                }}
              >
                Start Locking
              </Button>
              <Button
                variant="ghost"
                size="lg"
                onClick={() =>
                  document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })
                }
                className="px-8 py-6 text-base"
                style={{ color: 'hsl(35 8% 60%)', border: '1px solid hsl(35 12% 14%)' }}
              >
                How it works
              </Button>
            </div>
          </BlurFade>

        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, transparent, hsl(30 8% 4%))' }}
        />
      </section>

      {/* Price Ticker */}
      <PriceTicker />

      {/* How it works */}
      <section id="how-it-works" className="py-32 relative">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 60% 40% at 50% 50%, hsl(38 65% 57% / 0.04), transparent)',
          }}
        />
        <div className="container relative z-10">
          <BlurFade delay={0.1}>
            <div className="text-center mb-16">
              <span
                className="text-xs font-medium tracking-widest uppercase mb-3 block"
                style={{ color: 'hsl(38 65% 57%)' }}
              >
                The Process
              </span>
              <h2
                className="text-4xl md:text-5xl font-semibold"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(40 15% 92%)' }}
              >
                Lock, hold, give back
              </h2>
            </div>
          </BlurFade>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <HowItWorksStep
              number="I"
              title="Choose Token & Amount"
              description="Select SOL, BONK, SKR, MEW, USDC, GMT, or DRAGON — then decide how much to lock. A 1% fee is charged on deposit. Only 0.99 units enter the vault per 1 committed."
              icon={<TrendingUp size={16} />}
              delay={0.2}
            />
            <HowItWorksStep
              number="II"
              title="Set Your Timer"
              description="Choose a lock duration from 1 day to 5 years. The longer you commit, the stronger the signal. Preset options available."
              icon={<Clock size={16} />}
              delay={0.3}
            />
            <HowItWorksStep
              number="III"
              title="Lock & Hold"
              description="Your tokens are sealed in an on-chain vault. Unlock when the timer expires, or break early with a 1% penalty — paid as the price of impatience."
              icon={<Shield size={16} />}
              delay={0.4}
            />
            <HowItWorksStep
              number="IV"
              title="PermaLOCK"
              description="For the truly committed. PermaLOCK vaults have NO early unlock — ever. Confirmed with a 10-second hold. Absolute, irreversible conviction."
              icon={<Skull size={16} />}
              delay={0.5}
            />
            <HowItWorksStep
              number="V"
              title="Donate & Earn"
              description="Spread the wealth — donate tokens directly to fellow users. Choose your tier: 10, 25, 100, or all active users. Each donation earns Selfless Points with a multiplier based on how many you reach. ANGEL (AngelicSol) is a Solana meme coin launched on pump.fun built specifically with donations in mind — tip it freely."
              icon={<Heart size={16} />}
              delay={0.6}
            />
            <HowItWorksStep
              number="VI"
              title="Selected Users"
              description="Unlock exclusive Selected User slots as your Selfless Points grow. Favorite up to 10 addresses and always include them in every donation batch for a 30% bonus per selected user."
              icon={<Trophy size={16} />}
              delay={0.7}
            />
            <HowItWorksStep
              number="VII"
              title="Prize Pools"
              description="Contribute to monthly prize pools that reward the most selfless users. Three tiers: The Crown (top 1), The Elite (top 10 raffle), and The People's Pool (top 100 raffle). Pools draw on the 1st of every month."
              icon={<Coins size={16} />}
              delay={0.8}
            />
          </div>
        </div>
      </section>

      {/* Mini Leaderboard */}
      {top5.length > 0 && (
        <section className="py-20 relative">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse 50% 60% at 50% 50%, hsl(43 75% 68% / 0.04), transparent)',
            }}
          />
          <div className="container relative z-10">
            <BlurFade delay={0.1}>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <span
                    className="text-xs font-medium tracking-widest uppercase mb-2 block"
                    style={{ color: 'hsl(38 65% 57%)' }}
                  >
                    Hall of Discipline
                  </span>
                  <h2
                    className="text-3xl font-semibold"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(40 15% 92%)' }}
                  >
                    Selfless Leaderboard
                  </h2>
                </div>
                <Link
                  to="/leaderboard"
                  className="flex items-center gap-1.5 text-sm transition-colors"
                  style={{ color: 'hsl(38 65% 57%)' }}
                >
                  <Trophy size={14} />
                  View Full Leaderboard →
                </Link>
              </div>
            </BlurFade>

            <div className="max-w-2xl">
              {top5.map((entry, idx) => (
                <BlurFade key={entry.address} delay={0.1 + idx * 0.06}>
                  <div
                    className="flex items-center gap-4 px-5 py-3 rounded-lg mb-2"
                    style={{
                      background: idx === 0
                        ? 'linear-gradient(135deg, hsl(38 30% 8%), hsl(38 20% 10%))'
                        : 'hsl(30 6% 7%)',
                      border: idx === 0
                        ? '1px solid hsl(38 65% 57% / 0.35)'
                        : '1px solid hsl(35 12% 12%)',
                    }}
                  >
                    {/* Rank */}
                    <span
                      className="w-6 text-sm font-bold font-mono text-right shrink-0"
                      style={{
                        color: idx === 0
                          ? 'hsl(43 75% 68%)'
                          : idx === 1
                          ? 'hsl(210 20% 65%)'
                          : idx === 2
                          ? 'hsl(28 55% 48%)'
                          : 'hsl(35 8% 40%)',
                      }}
                    >
                      {idx + 1}
                    </span>

                    {/* Address */}
                    <span
                      className="font-mono text-sm flex-1 truncate"
                      style={{ color: 'hsl(40 15% 75%)' }}
                    >
                      {truncateAddress(entry.address)}
                    </span>

                    {/* Perma badge */}
                    {entry.permaCount > 0 && (
                      <span style={{ color: 'hsl(0 75% 60%)' }}>
                        <Skull size={12} />
                      </span>
                    )}

                    {/* Pending pricing badge */}
                    {entry.pendingCount > 0 && (
                      <span
                        style={{ color: 'hsl(35 8% 45%)' }}
                        title={`${entry.pendingCount} ${entry.pendingCount === 1 ? 'entry' : 'entries'} pending pricing`}
                      >
                        <Clock size={12} />
                      </span>
                    )}

                    {/* Points */}
                    <span
                      className="font-bold font-mono text-sm shrink-0"
                      style={{
                        color: idx === 0 ? 'hsl(43 75% 68%)' : 'hsl(38 65% 57%)',
                      }}
                    >
                      {formatPoints(entry.totalPoints)} pts
                    </span>
                  </div>
                </BlurFade>
              ))}

              <BlurFade delay={0.42}>
                <div className="mt-4">
                  <Link
                    to="/leaderboard"
                    className="text-sm transition-colors"
                    style={{ color: 'hsl(35 8% 45%)' }}
                  >
                    See all {leaderboard.length} participants →
                  </Link>
                </div>
              </BlurFade>
            </div>
          </div>
        </section>
      )}

      {/* CTA Banner */}
      <section className="py-24 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, hsl(38 65% 57% / 0.08) 0%, hsl(43 75% 68% / 0.04) 100%)',
            borderTop: '1px solid hsl(38 65% 57% / 0.15)',
            borderBottom: '1px solid hsl(38 65% 57% / 0.15)',
          }}
        />
        <div className="container relative z-10 text-center">
          <BlurFade delay={0.1}>
            <h2
              className="text-4xl md:text-5xl font-semibold mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(40 15% 92%)' }}
            >
              Ready to prove your worth?
            </h2>
            <p className="text-base mb-10 max-w-md mx-auto" style={{ color: 'hsl(35 8% 52%)' }}>
              The vault is open. Your discipline is the only key.
            </p>
            <Button
              size="lg"
              onClick={handleStart}
              className="px-12 py-6 text-base font-medium tracking-wide"
              style={{
                background: 'linear-gradient(135deg, hsl(38 65% 52%), hsl(43 75% 60%))',
                color: 'hsl(30 8% 4%)',
                border: 'none',
                boxShadow: '0 0 60px hsl(38 65% 57% / 0.25)',
              }}
            >
              Open the Vault
            </Button>
          </BlurFade>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="py-8 px-8 text-center"
        style={{ borderTop: '1px solid hsl(35 12% 14%)', color: 'hsl(35 8% 36%)' }}
      >
        <div className="flex items-center justify-center gap-2 mb-2">
          <Lock size={12} style={{ color: 'hsl(38 65% 57%)' }} />
          <span
            className="text-sm font-medium"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            SelfWorth
          </span>
        </div>
        <p className="text-xs">On-chain discipline. Built on Solana.</p>
      </footer>
    </PageLayout>
  );
};

export default HomePage;
